import Head from "next/head";
import { useRouter } from "next/router";
import { t, getLocaleFromPath, PUBLIC_LOCALES, LOCALES, DEFAULT_LOCALE, type SupportedLocale } from "@/lib/i18n";
import { getPublicPathname, pathWithoutLocalePrefix } from "@/lib/publicPath";
import { getProductsPathForLocale, isProductSlugPattern } from "@/lib/urls";

type SEOProps = {
  title?: string;
  description?: string;
  /** Absolute or path to og:image (will be made absolute) */
  image?: string;
  /** Override public pathname used for canonical / hreflang (e.g. canonical product URL). */
  canonicalPath?: string;
  /** Product JSON-LD or other structured data */
  jsonLd?: object | object[];
  noIndex?: boolean;
};

const SITE_NAME = "12GO Bricks";
const DEFAULT_TITLE = "12GO Bricks — 12GO building sets";
const DEFAULT_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://12gobricks.com";
const DEFAULT_OG_IMAGE = `${DEFAULT_URL.replace(/\/+$/, "")}/og-image.png`;

/** Dobre praktyki SEO: tytuł ≤60 znaków, opis 155–160 znaków w wynikach wyszukiwania */
const MAX_TITLE_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 160;

function truncateForMeta(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1).trim().replace(/\s+\S*$/, "") + "…";
}

function toAbsoluteUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  const base = DEFAULT_URL.replace(/\/+$/, "");
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${base}${path}`;
}

function toCanonicalUrl(publicPath: string): string {
  const base = DEFAULT_URL.replace(/\/+$/, "");
  return base + (publicPath === "/" ? "" : publicPath);
}

export function SEO({ title, description, image, canonicalPath, jsonLd, noIndex }: SEOProps) {
  const router = useRouter();
  const path = canonicalPath
    ? getPublicPathname(canonicalPath)
    : getPublicPathname(router.asPath || "/");
  const locale = getLocaleFromPath(path);
  const publicPath = getPublicPathname(path);
  const url = toCanonicalUrl(publicPath);

  const fullTitle = title
    ? title.includes(SITE_NAME) || title.includes("|") || /^12GO\b/.test(title)
      ? title
      : `${title} | ${SITE_NAME}`
    : DEFAULT_TITLE;
  const metaDescription = description || t(locale, "seo.description");

  const truncatedTitle = truncateForMeta(fullTitle, MAX_TITLE_LENGTH);
  const truncatedDescription = truncateForMeta(metaDescription, MAX_DESCRIPTION_LENGTH);
  const ogImage = image ? toAbsoluteUrl(image) : DEFAULT_OG_IMAGE;

  const locales = PUBLIC_LOCALES;

  /**
   * Mirrors Layout `getLocaleHref`: `getProductsPathForLocale` often returns a full path
   * that already includes `/${locale}/…` (e.g. `/de/sets`), so we must not prepend locale twice.
   */
  const buildHrefForLocale = (loc: SupportedLocale) => {
    const cleanBase = DEFAULT_URL.replace(/\/+$/, "");
    const pathWithoutLocale = pathWithoutLocalePrefix(path);
    const pathForSwitch = pathWithoutLocale === "/" ? "" : pathWithoutLocale;
    const segments = pathForSwitch.replace(/^\/+/, "").split("/").filter(Boolean);

    // Product pages always keep /{locale}/{slug}-n{id} (including /en/…).
    if (segments.length === 1 && isProductSlugPattern(segments[0])) {
      return `${cleanBase}/${loc}/${segments[0]}`
        .replace(/\/+/g, "/")
        .replace(":/", "://");
    }

    const localizedPath =
      segments.length > 0 ? getProductsPathForLocale(segments, locale, loc) : null;
    const fallback = pathForSwitch || "/";
    const targetPath = localizedPath ?? fallback;
    const targetWithLocale =
      loc === DEFAULT_LOCALE
        ? targetPath
        : targetPath.startsWith("/") &&
            LOCALES.includes(targetPath.split("/")[1] as SupportedLocale)
          ? targetPath
          : `/${loc}${targetPath === "/" ? "" : targetPath}`;
    return `${cleanBase}${targetWithLocale === "/" ? "" : targetWithLocale}`
      .replace(/\/+/g, "/")
      .replace(":/", "://");
  };

  const baseUrl = DEFAULT_URL.replace(/\/+$/, "");
  const defaultJsonLd: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: baseUrl,
      potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: `${baseUrl}/sets?q={search_term_string}` },
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: baseUrl,
      logo: `${baseUrl}/og-image.png`,
    },
  ];
  const customJsonLd = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];
  const jsonLdArray = [...defaultJsonLd, ...customJsonLd];

  return (
    <Head>
      <title>{truncatedTitle}</title>
      <meta name="description" content={truncatedDescription} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="robots" content={noIndex ? "noindex,nofollow" : "index,follow"} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={truncatedTitle} />
      <meta property="og:description" content={truncatedDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={truncatedTitle} />
      <meta property="og:locale" content={locale === "en" ? "en_US" : `${locale}_${locale.toUpperCase()}`} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={truncatedTitle} />
      <meta name="twitter:description" content={truncatedDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Canonical */}
      <link rel="canonical" href={url} />

      {/* hreflang */}
      {locales.map((loc) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={loc}
          href={buildHrefForLocale(loc)}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={buildHrefForLocale("en")}
      />

      <link rel="icon" type="image/png" href="/favicon.png" />

      {/* JSON-LD structured data */}
      {jsonLdArray.length > 0 &&
        jsonLdArray.map((ld, i) => (
          <script
            key={i}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
          />
        ))}
    </Head>
  );
}

